import { Check } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import { cn } from "@/shared/lib/cn";
import type {
  StepProgressItem,
  StepProgressNodeContentMode,
  StepProgressOrientation,
  StepProgressShape,
  StepProgressSize,
  StepProgressState,
} from "./step-progress-bar";

interface StepNodeProps {
  step: StepProgressItem;
  shape: StepProgressShape;
  size: StepProgressSize;
  nodeContentMode: StepProgressNodeContentMode;
  stepNumber: number;
}

export function StepNodeWithTooltip({
  step,
  shape,
  size,
  nodeContentMode,
  stepNumber,
  showTooltip,
}: StepNodeProps & { showTooltip: boolean }) {
  const side = size === "compact" ? 6 : 8;
  if (!showTooltip) {
    return (
      <StepNode
        step={step}
        shape={shape}
        size={size}
        nodeContentMode={nodeContentMode}
        stepNumber={stepNumber}
      />
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <StepNode
            step={step}
            shape={shape}
            size={size}
            nodeContentMode={nodeContentMode}
            stepNumber={stepNumber}
          />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={side}>
        <div className="space-y-0.5">
          <p className="font-medium">{step.title ?? step.label}</p>
          <p className="text-2xs text-background/80">{step.detail}</p>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function StepNode({ step, shape, size, nodeContentMode, stepNumber }: StepNodeProps) {
  const Icon = step.icon;
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center border transition-colors",
        shape === "circle" ? "rounded-full" : "rounded-md",
        size === "compact" ? "size-7" : "size-8",
        step.state === "done"
          ? "border-success/35 bg-success-bg text-success-fg"
          : step.state === "current"
            ? "border-success/40 bg-brand-soft-bg text-text-brand-strong ring-1 ring-success/30"
            : "border-border bg-background text-text-quaternary",
      )}
      aria-hidden
    >
      {step.state === "done" ? (
        <Check className={cn(size === "compact" ? "size-3.5" : "size-4")} />
      ) : nodeContentMode === "index" ? (
        <span
          className={cn("font-mono font-semibold", size === "compact" ? "text-2xs" : "text-xs")}
        >
          {stepNumber}
        </span>
      ) : (
        <Icon className={cn(size === "compact" ? "size-3.5" : "size-4")} />
      )}
    </span>
  );
}

export function StepConnector({
  currentState,
  nextState,
  orientation,
  size,
}: {
  currentState: StepProgressState;
  nextState: StepProgressState;
  orientation: StepProgressOrientation;
  size: StepProgressSize;
}) {
  const complete = currentState === "done" || nextState === "current";
  return (
    <span
      className={cn(
        "shrink-0 rounded-full",
        complete ? "bg-success/65" : "bg-border-strong/70",
        orientation === "horizontal"
          ? cn("mx-1.5 h-px", size === "compact" ? "w-3" : "w-4")
          : cn("my-1.5 w-px", size === "compact" ? "h-8" : "h-10"),
      )}
      aria-hidden
    />
  );
}

export function StepContent({
  step,
  orientation,
  size,
}: {
  step: StepProgressItem;
  orientation: StepProgressOrientation;
  size: StepProgressSize;
}) {
  const title = step.title ?? step.label;
  const description = step.description ?? step.detail;
  return (
    <div className={cn("min-w-0", orientation === "horizontal" ? "mx-2.5" : "pb-4")}>
      <p
        className={cn(
          "truncate font-semibold text-text-primary",
          size === "compact" ? "text-sm" : "text-base",
        )}
      >
        {title}
      </p>
      {description ? (
        <p
          className={cn(
            "truncate text-text-secondary",
            size === "compact" ? "mt-0.5 text-xs" : "mt-1 text-sm",
          )}
        >
          {description}
        </p>
      ) : null}
    </div>
  );
}
